import { ToasterService, AnalyticsService } from '#services';
import { ToastrMessage } from '#models';
import { environment } from '#environments';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private _toastrService = inject(ToasterService);
  private _translateService = inject(TranslateService);
  private _router = inject(Router);
  private _analyticsService = inject(AnalyticsService);

  handleError(err: unknown): void {
    // Only log to console in local development
    if (environment.envName === 'local') {
      console.error(err);
    }

    // Track exception to analytics
    if (err instanceof Error) {
      this._analyticsService.trackException(err, 3);
    } else if (typeof err === 'string') {
      this._analyticsService.trackException(err, 3);
    } else {
      this._analyticsService.trackException(new Error(String(err)), 3);
    }

    // status 0 = network-level failure (ERR_CONNECTION_REFUSED, server down, etc.)
    if (err instanceof HttpErrorResponse && err.status === 0) {
      this._router.navigate(['/500']);
      return;
    }

    const { detail, summary = '' } = this._handle(err);

    this._toastrService.showError(detail, summary);
  }

  private _handle(err: unknown): ToastrMessage {
    if (err instanceof HttpErrorResponse && err.status === 403) {
      return { detail: this._translateService.instant('common.forbidActionErrorMessage'), summary: '' };
    }

    if (err instanceof HttpErrorResponse && err.error) {
      const { ExceptionMessage = '', Title = '' } = err.error;

      if (Title !== '' && ExceptionMessage !== '') {
        return {
          detail: ExceptionMessage,
          summary: Title,
        };
      }
    }

    return {
      detail: this._translateService.instant('common.genericErrorMessage'),
      summary: '',
    };
  }
}
